import isFinite from 'lodash/isFinite'
import isInteger from 'lodash/isInteger'
import isNumber from 'lodash/isNumber'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import toNumber from 'lodash/toNumber'

import {
  BasisPoints,
  MAX_BASIS_POINTS,
  type BasisPointsFormatOptions,
  type BasisPointsOptions
} from '@klear/basis-points'
import { Money, divideRoundedMinor } from '@klear/money'

export type RatioOperand = number | { readonly amount: bigint | string | number }

export type BasisPointsInput =
  | number
  | string
  | BasisPoints
  | {
      value?: number | string | BasisPoints
    }

function coerceBasisPointsValue(input: BasisPointsInput, { allowNegative = false }: BasisPointsOptions = {}): number {
  let value: unknown = input
  while (isObject(value) && 'value' in value) {
    value = (value as { value: unknown }).value
  }

  const numeric = isString(value) ? toNumber(value) : value
  const min = allowNegative ? -MAX_BASIS_POINTS : 0

  if (!isNumber(numeric) || !isFinite(numeric) || !isInteger(numeric) || numeric < min || numeric > MAX_BASIS_POINTS) {
    throw new Error(`BasisPoints must be an integer between ${min} and ${MAX_BASIS_POINTS}: got ${value}`)
  }

  return numeric
}

function isRatioMoney(value: RatioOperand): value is { readonly amount: bigint | string | number } {
  return isObject(value) && 'amount' in value
}

function ratioOperandToMinor(value: RatioOperand): bigint | undefined {
  const raw = isRatioMoney(value) ? value.amount : value
  if (isBigint(raw)) return raw
  const text = isNumber(raw) ? `${raw}` : raw.toString()
  return /^-?\d+$/.test(text.trim()) ? BigInt(text.trim()) : undefined
}

function isBigint(value: unknown): value is bigint {
  // eslint-disable-next-line lodash/prefer-lodash-typecheck -- lodash has no isBigInt
  return typeof value === 'bigint'
}

function clampToBasisPoints(value: bigint, max: bigint): BasisPoints {
  if (value < 0n) return BasisPoints.ZERO
  const capped = value > max ? max : value
  return new BasisPoints(parseInt(capped.toString(), 10))
}

export function basisPointsFromInput(input: BasisPointsInput, opts: BasisPointsOptions = {}): BasisPoints {
  return input instanceof BasisPoints ? input : new BasisPoints(coerceBasisPointsValue(input, opts), opts)
}

export function basisPointsFromRatio(portion: RatioOperand, whole: RatioOperand): BasisPoints {
  if (portion instanceof Money && whole instanceof Money) {
    return BasisPoints.fromMoney(portion, whole)
  }
  const portionMinor = ratioOperandToMinor(portion)
  const wholeMinor = ratioOperandToMinor(whole)
  if (portionMinor == null || wholeMinor == null || wholeMinor <= 0n) return BasisPoints.ZERO
  const scale = BigInt(MAX_BASIS_POINTS)
  return clampToBasisPoints(divideRoundedMinor(portionMinor * scale, wholeMinor), scale)
}

export function formatBasisPoints(
  value: BasisPointsInput | undefined | null,
  options: BasisPointsFormatOptions = {}
): string {
  if (value == null) return '-'
  try {
    return basisPointsFromInput(value, { allowNegative: true }).toString(options)
  } catch {
    return '-'
  }
}

export function basisPointsToPercentString(input: BasisPointsInput, digits: number = 2): string {
  return basisPointsFromInput(input, { allowNegative: true }).toPercentString(digits)
}

export function parseBasisPoints(
  input: string | number | null | undefined,
  max: number = MAX_BASIS_POINTS
): BasisPoints | undefined {
  if (input == null) return undefined
  if (isNumber(input) && !isFinite(input)) return undefined

  const cleaned = (isNumber(input) ? `${input}` : input).replace(/[%,\s]/g, '')
  if (!/^-?\d*(\.\d*)?$/.test(cleaned) || cleaned === '' || cleaned === '-' || cleaned === '.') return undefined

  const negative = cleaned.startsWith('-')
  const [whole = '0', fraction = ''] = (negative ? cleaned.slice(1) : cleaned).split('.')
  const fractionScale = 10n ** BigInt(Math.max(0, fraction.length - 2))
  const hundredths = BigInt(whole || '0') * 100n + divideRoundedMinor(BigInt(fraction.padEnd(2, '0')), fractionScale)
  const normalizedMax = BigInt(Math.min(MAX_BASIS_POINTS, Math.max(0, Math.round(max))))
  return clampToBasisPoints(negative ? -hundredths : hundredths, normalizedMax)
}
